import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Safely check if Service Worker & Push Notification are supported in current context (requires HTTPS or localhost)
const isMessagingSupported = () => {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        Boolean(navigator.serviceWorker) &&
        'PushManager' in window &&
        'Notification' in window &&
        (window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    );
};

let messaging = null;
if (isMessagingSupported()) {
    try {
        messaging = getMessaging(app);
    } catch (err) {
        console.warn("Firebase Messaging initialization failed:", err);
        messaging = null;
    }
} else {
    console.warn("Firebase Messaging disabled: Push notifications require a secure context (HTTPS) or supported browser.");
}

export const requestForToken = async () => {
    try {
        if (!messaging) {
            console.log("Firebase Messaging is not available (e.g. non-HTTPS or unsupported browser).");
            return null;
        }

        if (!('Notification' in window)) {
            console.log("This browser does not support desktop notification");
            return null;
        }

        if (Notification.permission === 'denied') {
            console.log("Notification permission is denied by the user. Push notifications will not work.");
            return null;
        }

        // 1. Manually register Service Worker with config params
        if ('serviceWorker' in navigator && navigator.serviceWorker) {
            const params = new URLSearchParams({
                apiKey: firebaseConfig.apiKey || "",
                authDomain: firebaseConfig.authDomain || "",
                projectId: firebaseConfig.projectId || "",
                storageBucket: firebaseConfig.storageBucket || "",
                messagingSenderId: firebaseConfig.messagingSenderId || "",
                appId: firebaseConfig.appId || ""
            }).toString();

            const registration = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/firebase-messaging-sw.js?${params}&v=2`);

            // 2. Pass the registration to getToken
            const currentToken = await getToken(messaging, {
                vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
                serviceWorkerRegistration: registration,
            });

            if (currentToken) {
                // console.log("Current token for client: ", currentToken);
                localStorage.setItem('fcmToken', currentToken);
                return currentToken;
            } else {
                console.log("No registration token available. Request permission to generate one.");
                return null;
            }
        }

        return null;
    } catch (err) {
        if (err.code === 'messaging/permission-blocked') {
            console.log("Notification permission was blocked. Please enable it in browser settings.");
        } else {
            console.error("An error occurred while retrieving token: ", err);
        }
        return null;
    }
};

export const onMessageListener = (callback) => {
    if (!messaging) {
        return () => {}; // return no-op unsubscribe function
    }
    try {
        return onMessage(messaging, (payload) => {
            console.log("Payload received: ", payload);
            callback(payload);
        });
    } catch (err) {
        console.warn("Error attaching onMessage listener:", err);
        return () => {};
    }
};

export default app;